import React, { useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Label,
  Input,
  FormFeedback,
  Col,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { useCreateProduct } from "hooks/useProduct";
import { productValidationSchema } from "utils";
import { ProductType } from "types";
import { useGetSuppliers } from "hooks/useSuppliers";
import { useGetBranches } from "hooks/useBranches";
import { useGetCategories } from "hooks/useCategories";
import { toast } from "react-toastify";
import { BaseButton } from "Components";

export const AddNewProduct = ({ isOpen, toggle, data, newProduct }) => {
  const { mutate, isPending } = useCreateProduct();
  const { data: branchData } = useGetBranches();
  const { data: categoryData } = useGetCategories();
  const { data: supplierData } = useGetSuppliers();

  const handleCancelClick = () => {
    formik.resetForm();
    toggle();
  };

  const initialValues = {
    name: "",
    description: "",
    cost_price: 0,
    selling_price: 0,
    sku: "",
    barcode: "",
    allow_taxes: false,
    weight: 0,
    weight_unit: "",
    quantity: 0,
    reorder_quantity: 0,
    category_id: 0,
    branch_id: 0,
    supplier_id: 0,
    sell_oos: false,
    expiry_date: "",
    files: [],
  };

  const formik = useFormik<Partial<ProductType>>({
    initialValues: initialValues,
    validationSchema: productValidationSchema,
    onSubmit: async (values) => {
      // Build the payload
      console.log("Product: ", values);
      const payload: any = {
        name: values.name,
        description: values.description,
        cost_price: values.cost_price,
        selling_price: values.selling_price,
        sku: values.sku,
        allow_taxes: values.allow_taxes,
        weight: values.weight,
        weight_unit: values.weight_unit,
        quantity: values.quantity,
        reorder_quantity: values.reorder_quantity,
        category_id: values.category_id,
        branch_id: values.branch_id,
        supplier_id: values.supplier_id,
        sell_oos: values.sell_oos,
        files: values.files,
      };

      // Conditionally add barcode and expiry_date if they are provided
      if (values.barcode) {
        payload.barcode = values.barcode;
      }
      if (values.expiry_date) {
        payload.expiry_date = values.expiry_date;
      }

      // Use the mutate function from your hook
      mutate(payload, {
        onSuccess: (data) => {
          newProduct(data);
          toggle();
          toast.success("Product created successfully");
          formik.resetForm();
          // setLoading(false);
        },
        onError: (error) => {
          // setLoading(false);
        },
      });
    },
  });

  useEffect(() => {
    formik.values.name = data.name;
    formik.values.quantity = data.quantity;
    formik.values.branch_id = data.branch_id;
  }, [data]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Add New Product</ModalHeader>
      <ModalBody className="px-3">
        <Form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          autoComplete="off"
        >
          <Row>
            {/* product name */}
            <Col sm="6">
              <div className="mb-3">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.name && !!formik.errors.name}
                />
                {formik.touched.name && formik.errors.name && (
                  <FormFeedback>{formik.errors.name}</FormFeedback>
                )}
              </div>
            </Col>

            {/* category */}
            <Col sm="6">
              <div className="mb-3">
                <Label htmlFor="category_id">Category *</Label>
                <Input
                  type="select"
                  name="category_id"
                  onChange={(e) =>
                    formik.setFieldValue("category_id", e.target.value)
                  }
                  value={formik.values.category_id}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(formik.touched.category_id && formik.errors.category_id)
                  }
                >
                  <option value="">Select Category...</option>
                  {categoryData &&
                    categoryData.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                </Input>
                {formik.touched.category_id && formik.errors.category_id && (
                  <FormFeedback>{formik.errors.category_id}</FormFeedback>
                )}
              </div>
            </Col>
            {/* description */}
            <Col sm="12">
              <div className="mb-3">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  type="textarea"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.description && !!formik.errors.description
                  }
                />
                {formik.touched.description && formik.errors.description && (
                  <FormFeedback>{formik.errors.description}</FormFeedback>
                )}
              </div>
            </Col>
            {/* cost price */}
            <Col sm="6">
              <div className="mb-3">
                <Label htmlFor="cost_price">Cost Price *</Label>
                <Input
                  id="cost_price"
                  name="cost_price"
                  type="number"
                  value={formik.values.cost_price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.cost_price && !!formik.errors.cost_price
                  }
                />
                {formik.touched.cost_price && formik.errors.cost_price && (
                  <FormFeedback>{formik.errors.cost_price}</FormFeedback>
                )}
              </div>
            </Col>

            {/* selling price */}
            <Col sm="6">
              <div className="mb-3">
                <Label htmlFor="selling_price">Selling Price *</Label>
                <Input
                  id="selling_price"
                  name="selling_price"
                  type="number"
                  value={formik.values.selling_price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.selling_price &&
                    !!formik.errors.selling_price
                  }
                />
                {formik.touched.selling_price &&
                  formik.errors.selling_price && (
                    <FormFeedback>{formik.errors.selling_price}</FormFeedback>
                  )}
              </div>
            </Col>
          </Row>

          <Row>
            {/* stock keeping unit */}
            <Col sm="6">
              <div className="mb-3">
                <Label htmlFor="sku">
                  SKU
                  <sub style={{ fontSize: "9px" }}>(Stock Keeping Unit)</sub>
                </Label>
                <Input
                  id="sku"
                  name="sku"
                  type="text"
                  value={formik.values.sku}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.sku && !!formik.errors.sku}
                />
                {formik.touched.sku && formik.errors.sku && (
                  <FormFeedback>{formik.errors.sku}</FormFeedback>
                )}
              </div>
            </Col>

            {/* branch  */}
            <Col sm="6">
              <div className="mb-3">
                <Label htmlFor="branch_id">Branch *</Label>
                <Input
                  type="select"
                  name="branch_id"
                  onChange={(e) =>
                    formik.setFieldValue("branch_id", e.target.value)
                  }
                  value={formik.values.branch_id}
                  invalid={
                    !!(formik.touched.branch_id && formik.errors.branch_id)
                  }
                >
                  <option value="">Select Branch...</option>
                  {branchData &&
                    branchData.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                </Input>
                {formik.touched.branch_id && formik.errors.branch_id && (
                  <FormFeedback>{formik.errors.branch_id}</FormFeedback>
                )}
              </div>
            </Col>

            <Col sm="6">
              <div className="mb-3">
                <Label htmlFor="supplier_id">Supplier *</Label>
                <Input
                  type="select"
                  name="supplier_id"
                  onChange={(e) =>
                    formik.setFieldValue("supplier_id", e.target.value)
                  }
                  value={formik.values.supplier_id}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(formik.touched.supplier_id && formik.errors.supplier_id)
                  }
                >
                  <option value="">Select Supplier...</option>
                  {supplierData &&
                    supplierData.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                </Input>
                {formik.touched.supplier_id && formik.errors.supplier_id && (
                  <FormFeedback>{formik.errors.supplier_id}</FormFeedback>
                )}
              </div>
            </Col>
            <Col sm="6">
              <div className="mb-3">
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  name="quantity"
                  type="number"
                  value={formik.values.quantity}
                  onChange={formik.handleChange}
                  invalid={formik.touched.quantity && !!formik.errors.quantity}
                />
                {formik.touched.quantity && formik.errors.quantity && (
                  <FormFeedback>{formik.errors.quantity}</FormFeedback>
                )}
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <BaseButton
          color="primary"
          type="submit"
          disabled={isPending}
          loading={isPending}
          onClick={(e) => {
            e.preventDefault();
            console.log("Form submission triggered");
            formik.handleSubmit();
          }}
        >
          Create
        </BaseButton>
        <BaseButton
          color="secondary"
          onClick={() => {
            handleCancelClick();
          }}
        >
          Cancel
        </BaseButton>
      </ModalFooter>
    </Modal>
  );
};
